/** Map container ids used across search / hybrid / area-page-v2. */
export const SEARCH_MAP_ELEMENT_ID = 'search-map';
export const AREA_PAGE_V2_MAP_ELEMENT_ID = 'area-page-v2-map';

const KNOWN_MAP_ELEMENT_IDS = [SEARCH_MAP_ELEMENT_ID, AREA_PAGE_V2_MAP_ELEMENT_ID] as const;

export const HYBRID_MARGIN_PX = 16;
const PIN_CALLOUT_GAP = 16;
const MAX_POSITION_ATTEMPTS = 20;
/** Above MapV2Controls (`$z-index-bump`) so callouts paint over zoom/satellite buttons. */
const MAPKIT_VIEW_Z_INDEX_WHILE_CALLOUT_OPEN = '11';
const CALLOUT_ANCESTOR_Z_INDEX = '9999';
const ACCESSIBILITY_WIDGET_SELECTOR = '.accessibility-widget-wrapper';

let calloutElevationCleanup: (() => void) | null = null;
let calloutRemovalTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Walk up from the callout to the map container and raise stacking so the popover
 * escapes MapKit transform contexts and paints above map chrome (zoom controls).
 */
export function elevateCalloutAbovePageUI(calloutDiv: HTMLDivElement) {
  calloutElevationCleanup?.();
  if (calloutRemovalTimer) {
    clearInterval(calloutRemovalTimer);
  }

  const restoreFns: (() => void)[] = [];

  requestAnimationFrame(() => {
    let el = calloutDiv.parentElement;
    while (el && !isKnownMapElementId(el.id) && !el.classList.contains('mk-map-view')) {
      const ref = el;
      const savedZ = el.style.zIndex;
      el.style.zIndex = CALLOUT_ANCESTOR_Z_INDEX;
      restoreFns.push(() => { ref.style.zIndex = savedZ; });
      el = el.parentElement;
    }
    if (el?.classList.contains('mk-map-view')) {
      const ref = el;
      const savedZ = el.style.zIndex;
      el.style.zIndex = MAPKIT_VIEW_Z_INDEX_WHILE_CALLOUT_OPEN;
      restoreFns.push(() => { ref.style.zIndex = savedZ; });
    }
  });

  const widget = document.querySelector(ACCESSIBILITY_WIDGET_SELECTOR) as HTMLElement | null;
  if (widget) {
    const saved = widget.style.zIndex;
    widget.style.zIndex = '0';
    restoreFns.push(() => { widget.style.zIndex = saved; });
  }

  const restore = () => {
    restoreFns.forEach(fn => fn());
    restoreFns.length = 0;
  };

  calloutElevationCleanup = restore;

  calloutRemovalTimer = setInterval(() => {
    if (!calloutDiv.isConnected) {
      restore();
      calloutElevationCleanup = null;
      if (calloutRemovalTimer) {
        clearInterval(calloutRemovalTimer);
        calloutRemovalTimer = null;
      }
    }
  }, 300);
}

export function isKnownMapElementId(id: string | null | undefined): boolean {
  return id != null && (KNOWN_MAP_ELEMENT_IDS as readonly string[]).includes(id);
}

/** Resolve the map container for callout clamping (search map or Area Page V2). */
export function resolveMapElement(): HTMLElement | null {
  for (const id of KNOWN_MAP_ELEMENT_IDS) {
    const el = document.getElementById(id);
    if (el) {
      return el;
    }
  }
  return null;
}

export function hybridBottomLimitPx(): number {
  const mapEl = resolveMapElement();
  if (!mapEl || typeof window === 'undefined') {
    return typeof window !== 'undefined' ? window.innerHeight : 0;
  }
  const mapRect = mapEl.getBoundingClientRect();
  const vv = window.visualViewport;
  const viewportBottom = vv
    ? Math.min(vv.offsetTop + vv.height, window.innerHeight)
    : window.innerHeight;
  return Math.min(mapRect.bottom, viewportBottom);
}

/**
 * MapKit pans the map to fit the element returned from `calloutElementForAnnotation`.
 * Wrapping the real callout in a zero-size anchor means there is never anything to
 * fit, so selecting a pin never moves the map; the content is absolutely positioned
 * inside the anchor by `positionCalloutContent`.
 */
export function createCalloutAnchor(content: HTMLDivElement): HTMLDivElement {
  const anchor = document.createElement('div');
  anchor.style.position = 'relative';
  anchor.style.width = '0';
  anchor.style.height = '0';
  anchor.style.overflow = 'visible';
  content.style.transform = 'none';
  content.style.opacity = '0';
  anchor.appendChild(content);
  return anchor;
}

/**
 * Prefer opening to the left of the pin (Area Page map sits on the left; listings
 * on the right). Fall back to the right only when left cannot fit.
 */
export function computeDesiredPosition(
  pinEl: HTMLElement,
  mapEl: HTMLElement,
  size: { width: number; height: number },
  isHybridPage = false
) {
  const pinRect = pinEl.getBoundingClientRect();
  const mapRect = mapEl.getBoundingClientRect();
  const margin = HYBRID_MARGIN_PX;
  const bottomLimit = isHybridPage ? hybridBottomLimitPx() : mapRect.bottom;

  const pinCX = pinRect.left + pinRect.width / 2;
  const pinCY = pinRect.top + pinRect.height / 2;

  const spaceLeft = pinRect.left - mapRect.left - margin;
  const spaceRight = mapRect.right - pinRect.right - margin;

  const canFitRight = spaceRight >= size.width + PIN_CALLOUT_GAP;
  const canFitLeft = spaceLeft >= size.width + PIN_CALLOUT_GAP;

  let desiredLeft: number;
  if (canFitLeft) {
    desiredLeft = pinRect.left - PIN_CALLOUT_GAP - size.width;
  } else if (canFitRight) {
    desiredLeft = pinRect.right + PIN_CALLOUT_GAP;
  } else {
    desiredLeft = spaceLeft >= spaceRight
      ? mapRect.left + margin
      : mapRect.right - margin - size.width;
  }

  desiredLeft = Math.max(mapRect.left + margin, Math.min(desiredLeft, mapRect.right - margin - size.width));

  let desiredTop = pinCY - size.height / 2;
  const topBar = document.querySelector('[class*="top-bar"]') as HTMLElement | null;
  const topBarBottom = topBar ? topBar.getBoundingClientRect().bottom : mapRect.top;
  const effectiveTop = Math.max(mapRect.top, topBarBottom);
  const topBound = effectiveTop + margin;
  const botBound = bottomLimit - margin - size.height;
  desiredTop = Math.max(topBound, Math.min(desiredTop, botBound));

  return { desiredLeft, desiredTop, pinCX, pinCY };
}

/**
 * Place the callout beside its pin, hosted on the map root container so it stacks above zoom/satellite buttons without raising MapKit.
 */
export function positionCalloutContent(content: HTMLDivElement, annotation: mapkit.Annotation, isHybridPage = false) {
  let attempts = 0;
  const attempt = () => {
    if (!content.isConnected && content.dataset.calloutHosted !== 'true') {
      return;
    }

    const pinEl = annotation.element;
    const mapEl = resolveMapElement();
    if (!(pinEl instanceof HTMLElement) || !mapEl) {
      content.style.opacity = '1';
      return;
    }

    const rect = content.getBoundingClientRect();
    if (rect.width === 0 && attempts < MAX_POSITION_ATTEMPTS) {
      attempts++;
      requestAnimationFrame(attempt);
      return;
    }

    hostCalloutOnMapRoot(content, mapEl);

    const mapRect = mapEl.getBoundingClientRect();
    const { desiredLeft, desiredTop } = computeDesiredPosition(
      pinEl,
      mapEl,
      { width: rect.width || content.offsetWidth, height: rect.height || content.offsetHeight },
      isHybridPage
    );
    content.style.left = `${Math.round(desiredLeft - mapRect.left)}px`;
    content.style.top = `${Math.round(desiredTop - mapRect.top)}px`;
    content.style.opacity = '1';
  };
  requestAnimationFrame(attempt);
}

const hostedCalloutCleanups = new WeakMap<HTMLDivElement, () => void>();

/**
 * Move callout content onto the map container
 */
function hostCalloutOnMapRoot(content: HTMLDivElement, mapEl: HTMLElement) {
  if (content.dataset.calloutHosted === 'true' && content.parentElement === mapEl) {
    return;
  }

  const anchor = content.parentElement;
  mapEl.appendChild(content);
  content.dataset.calloutHosted = 'true';
  content.style.position = 'absolute';

  hostedCalloutCleanups.get(content)?.();

  if (!(anchor instanceof HTMLElement)) {
    return;
  }

  const cleanup = () => {
    if (content.isConnected) {
      content.remove();
    }
    hostedCalloutCleanups.delete(content);
  };

  const observer = new MutationObserver(() => {
    if (!anchor.isConnected) {
      observer.disconnect();
      cleanup();
    }
  });
  observer.observe(mapEl, { childList: true, subtree: true });

  const timer = setInterval(() => {
    if (!anchor.isConnected) {
      clearInterval(timer);
      observer.disconnect();
      cleanup();
    }
  }, 300);

  hostedCalloutCleanups.set(content, () => {
    clearInterval(timer);
    observer.disconnect();
  });
}
